// app/booking2/components/BookingSummary.tsx
import React from 'react';
import Image from 'next/image';
import {format} from 'date-fns';
import {de} from 'date-fns/locale';
import {PhoneIcon, UserCircleIcon, CurrencyEuroIcon} from '@heroicons/react/24/outline';
import {CalendarWeek, Clock, Messages, Users} from "flowbite-react-icons/outline";
import {BookingGuide, Product} from '@/app/api/types';
import {formatPricePerMinute} from "@/app/lib/utils";

interface BookingSummaryProps {
  guide: BookingGuide;
  product: Product;
  selectedDate: Date | null;
  selectedTimeSlot: string | null;
  timezone: string;
  onConfirm: () => void;
  buttonText: string;
  disabled?: boolean;
}

export const BookingSummary: React.FC<BookingSummaryProps> = ({
                                                                guide,
                                                                product,
                                                                selectedDate,
                                                                selectedTimeSlot,
                                                                timezone,
                                                                onConfirm,
                                                                buttonText,
                                                                disabled = false,
                                                              }) => {
  const formatDateTime = (date: Date, timeSlot: string) => {
    const [hours, minutes] = timeSlot.split(':').map(Number);
    const dateTime = new Date(date);
    dateTime.setHours(hours, minutes, 0, 0);
    return format(dateTime, "EEEE, d. MMMM yyyy, HH:mm 'Uhr'", {locale: de});
  };

  return (
    <div className="self-stretch flex-col justify-end items-center gap-2 flex pt-0">
      <div className="self-stretch flex justify-between gap-2">
        <div className="flex-1">
          <h2 className="text-[#111928] text-sm font-semibold mb-2">DEIN TERMIN IM ÜBERBLICK</h2>
          <div className="flex items-center mb-2">
            {guide.picture ? (
              <div className="h-10 w-10 overflow-hidden rounded-full mr-2">
                <Image
                  src={guide.picture}
                  alt={`Profile picture of ${guide.fullName}`}
                  width={40}
                  height={40}
                  className="h-full w-full object-cover"
                />
              </div>
            ) : (
              <UserCircleIcon className="w-10 h-10 text-gray-400 mr-2" aria-hidden="true"/>
            )}
            <p>{guide.given_name}</p>
          </div>
          <div className="flex items-center mb-2">
            <Users className="mr-2 w-5 h-5"/>
            <p>{product.name}</p>
          </div>
          <div className="flex items-center mb-2">
            <Clock className="mr-2 w-5 h-5"/>
            <p>{product.duration} Minuten</p>
          </div>
          {selectedDate && selectedTimeSlot ? (
            <div className="flex items-center mb-2">
              <CalendarWeek className="mr-2 w-5 h-5"/>
              <p>{formatDateTime(selectedDate, selectedTimeSlot)}</p>
            </div>
          ) : (
            <div className="flex items-center mb-2">
              <CalendarWeek className="mr-2 w-5 h-5"/>
              <p className="text-gray-400">Bitte wähle einen Termin aus</p>
            </div>
          )}
          <div className="flex items-center mb-2">
            <CurrencyEuroIcon className="mr-2 w-5 h-5"/>
            <p>
              {parseFloat(product.price).toLocaleString('de-DE', {
                style: 'currency',
                currency: 'EUR',
              })}
            </p>
          </div>
          {product.duration > 0 && (
            <p className="text-sm text-gray-500 italic mb-2 ml-[26px]">
              entspricht {formatPricePerMinute(parseFloat(product.price), product.duration)} € pro Minute
            </p>
          )}
          <p className="text-xs text-gray-400 ml-[26px]">
            Die Zeit wird verbindlich für dich reserviert.
          </p>

        </div>
      </div>
      <p className="text-sm text-gray-600 dark:text-gray-400 text-center mb-2">
      Mit Klick auf den Button erklärst du dich mit einer <strong>zahlungspflichtigen Buchung</strong> einverstanden.
      </p>
      <button
        data-analytics-id="funnel-product-initiateCheckout"
        onClick={onConfirm}
        disabled={disabled}
        className={`self-stretch px-5 pt-1 h-8 text-xl rounded-full font-bold justify-center items-center gap-2 inline-flex leading-[21px] ${
          disabled
            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
            : 'bg-primary text-white hover:bg-primary-dark'
        }`}
      >
        {buttonText}
      </button>
      <p className="text-sm text-gray-600 dark:text-gray-400 text-center mt-2">
        Bitte verlasse diese Seite nicht oder schließe nicht das Browserfenster während der Zahlung.
        Du wirst im nächsten Schritt zu unserem Zahlungsanbieter Stripe weitergeleitet.
      </p>
    </div>
  );
};